/**
 * EAA-1670: async auth orchestration — replaces `authSaga.ts`'s takeLatest
 * handlers (see docs/plans/fe-restructure/phase-7-state-migration.md). Each
 * function here does exactly what its saga handler did, imperatively: call
 * `authService`/`tenantService`, then update `useAuthStore` directly.
 *
 * The two `window` event listeners below (`auth:logout`, `auth:embed-invalid`
 * — dispatched by the api client on an unrecoverable 401, see
 * services/api/client.ts) replace the saga's `fork`-ed `eventChannel`
 * watchers, which ran for the app's whole lifetime; registering them here at
 * module scope achieves the same "runs once, forever" behavior, since this
 * module is only ever evaluated once (ES module caching).
 *
 * EAA-1762: `login`/`checkAuth`/`logout` were rewritten off password auth
 * onto Keycloak OIDC (`@/auth/authClient`) — see that module + `auth.service.ts`
 * for the token-source swap. `authMode` is always 'staff' for a non-embed
 * OIDC session; the store shape and every other consumer (AuthGuard,
 * useWebContentPermissions, DashboardGuard) are unchanged.
 *
 * EAA-1762 addendum: in `authConfig.mode === 'password'` (VITE_AUTH_MODE,
 * UAT-only), `tenantLogin()` restores the tenant-portal password path
 * alongside `loginWithPassword()` — `authMode` can then also be 'tenant',
 * exactly as it could before EAA-1762.
 */

import { authClient } from '@/auth/authClient';
import { authConfig } from '@/auth/authConfig';
import { authService } from '@/services/auth.service';
import { tenantService, type Tenant } from '@/services/tenant.service';
import { useAuthStore, type LoginFailurePayload } from '@/store/authStore';
import { queryClient } from '@/lib/queryClient';
import type { User } from '@/services/auth.service';
import type { LoginPayload, TenantPortalLoginPayload } from './authTypes';
import { coerceApiError } from '@/utils/apiValidationErrors';
import {
    clearEmbedApiToken,
    isLikelyTenantJwt,
    setEmbedApiToken,
    setEmbedBearerToken,
} from '@/utils/embedApiToken';

// EAA-1762 / EAA-1765: the terminating condition for a sign-in that cannot complete.
// A tab that keeps failing would otherwise re-enter login() on every load and redirect
// forever, each lap a full page load. (Observed with a backend that 401'd `/auth/me` for
// SSO bearers.) After MAX_LOGIN_ATTEMPTS asks inside the window we STOP and surface an
// error, so a misconfiguration shows up as a message instead of an infinite reload.
const LOGIN_ATTEMPTS_KEY = 'eaa1762:login-attempts';
const MAX_LOGIN_ATTEMPTS = 2;
const LOGIN_ATTEMPT_WINDOW_MS = 60_000;

// EAA-1878: the counter above still could not terminate the loop that actually
// froze the tab, because `handleOidcCallback` DELETES it on every successful
// exchange. Any cycle whose callback succeeds therefore restarts with a full
// budget, and the cycle that matters looks exactly like that:
//
//   /callback succeeds -> navigate to the app -> something rejects the session
//   (e.g. `/auth/me` 401s for this subject) -> AuthGuard -> /login -> login()
//   with a freshly-cleared budget -> portal -> Keycloak -> /callback succeeds...
//
// Each lap is a full page load, so the tab spends its life reloading — reported
// as "callback too much, tab freezes". The attempt COUNT cannot see it; only the
// fact that we are asking to sign in again moments after a sign-in demonstrably
// completed can. That is what this marker records.
const CALLBACK_COMPLETED_KEY = 'eaa1878:callback-completed-at';

// EAA-1878: and a hard ceiling on /callback ARRIVALS, which is the thing the
// reporter actually sees ("callback too much, the tab freezes"). The marker
// above catches the loop whose lap goes back through `login()`; this catches it
// wherever the lap starts, because every lap must pass through here to exchange
// a code. Deliberately independent of `login()`'s counter: a lap that never
// re-enters `login()` — the app itself deciding to re-authenticate, a provider
// re-issuing a redirect — would otherwise be invisible to it.
const CALLBACK_ARRIVALS_KEY = 'eaa1878:callback-arrivals';
const MAX_CALLBACK_ARRIVALS = 3;

/** Counts this arrival and reports whether the tab is now provably looping. */
function recordCallbackArrival(): boolean {
    const raw = sessionStorage.getItem(CALLBACK_ARRIVALS_KEY);
    let count = 0;
    let firstAt = 0;
    if (raw) {
        try {
            const parsed = JSON.parse(raw) as { count?: number; firstAt?: number };
            if (typeof parsed.count === 'number' && typeof parsed.firstAt === 'number') {
                count = parsed.count;
                firstAt = parsed.firstAt;
            }
        } catch {
            // Unreadable value is treated as no history, same as `readLoginAttempts`.
        }
    }
    // Outside the window the previous arrivals are unrelated history — a person
    // signing in again ten minutes later is not a loop.
    if (firstAt && Date.now() - firstAt > LOGIN_ATTEMPT_WINDOW_MS) {
        count = 0;
        firstAt = 0;
    }
    const next = { count: count + 1, firstAt: firstAt || Date.now() };
    sessionStorage.setItem(CALLBACK_ARRIVALS_KEY, JSON.stringify(next));
    return next.count > MAX_CALLBACK_ARRIVALS;
}

function markCallbackCompleted(): void {
    sessionStorage.setItem(CALLBACK_COMPLETED_KEY, String(Date.now()));
}

/** True when a code exchange completed within the loop window — i.e. we hold a
 *  fresh, valid Keycloak session and are being asked to obtain one again. */
function signInJustCompleted(): boolean {
    const raw = sessionStorage.getItem(CALLBACK_COMPLETED_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at <= LOGIN_ATTEMPT_WINDOW_MS;
}

/** Thrown instead of redirecting once the sign-in round trip is provably looping. */
export class SignInLoopError extends Error {
    constructor() {
        super('Sign-in did not complete after repeated attempts.');
        this.name = 'SignInLoopError';
    }
}

interface LoginAttempts {
    count: number;
    firstAt: number;
}

function readLoginAttempts(): LoginAttempts {
    const raw = sessionStorage.getItem(LOGIN_ATTEMPTS_KEY);
    if (!raw) return { count: 0, firstAt: 0 };
    try {
        const parsed = JSON.parse(raw) as Partial<LoginAttempts>;
        if (typeof parsed.count !== 'number' || typeof parsed.firstAt !== 'number') {
            return { count: 0, firstAt: 0 };
        }
        // Outside the window the previous burst is unrelated history, not a loop.
        if (Date.now() - parsed.firstAt > LOGIN_ATTEMPT_WINDOW_MS) {
            return { count: 0, firstAt: 0 };
        }
        return { count: parsed.count, firstAt: parsed.firstAt };
    } catch {
        return { count: 0, firstAt: 0 };
    }
}

function recordLoginAttempt(): void {
    const prev = readLoginAttempts();
    const next: LoginAttempts = {
        count: prev.count + 1,
        firstAt: prev.firstAt || Date.now(),
    };
    sessionStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(next));
}

/** Called once a session is actually established — the loop, if any, is over. */
export function clearLoginAttempts(): void {
    sessionStorage.removeItem(LOGIN_ATTEMPTS_KEY);
    // EAA-1878: the completion marker is part of the same budget and must go with
    // it. This function is what "Thử lại" and "Đăng xuất" call — a person saying
    // "the condition is fixed, try again" — and leaving the marker behind would
    // make the very next `login()` throw the loop error they just dismissed.
    sessionStorage.removeItem(CALLBACK_COMPLETED_KEY);
    sessionStorage.removeItem(CALLBACK_ARRIVALS_KEY);
}

/**
 * `/login` — EAA-1765 (revised 2026-08-26): this app asks Keycloak for a token with
 * **its own client**, directly. One `signinRedirect()`, no detour.
 *
 * REVERSES the portal-first flow this function used to implement: bounce to
 * `${portalUrl}/login?returnTo=<deep link>`, let the portal sign in with `portal-web`
 * against `3002/callback`, come back, and only then mint this app's own token. That was
 * three extra full page loads to reach an authorize request the realm SSO cookie answers
 * on the spot, and it put the WRONG app's name on the Keycloak screen — a person opening
 * :3000 was asked to sign in to "EMG Portal".
 *
 * The rule it was protecting ("the login form only ever appears at the portal") no longer
 * buys anything: it is the same Keycloak form on the same realm either way, and the login
 * theme now names the destination app in its own hero (EAA-1877), so going direct shows
 * "AI Content Studio" where the detour showed "EMG Portal". The detour made the branding
 * worse, not better.
 *
 * What this deletes, and why none of it is missed: `PORTAL_BOUNCE_KEY` (which leg of the
 * detour are we on), `eaa1765:sso-established` + `eaa1765:silent-probe` (the `prompt=none`
 * probe added to SKIP the detour when possible), and the `login_required` fallback branch
 * in `handleOidcCallback`. All of it existed to manage a detour that no longer happens —
 * and the portal ⇄ web-content ping-pong recorded in §6 of the SSO plan is now
 * structurally impossible, because there is no portal leg to ping-pong with.
 *
 * The loop guards STAY: `signInJustCompleted()`, the attempt budget, and the callback
 * arrival cap all still bound a sign-in that cannot complete for other reasons (a backend
 * that 401s `/auth/me`, a subject the app rejects).
 *
 * `returnTo` is an app-relative path, stashed in the OIDC `state` and read back by
 * `handleOidcCallback`.
 */
export async function login(returnTo?: string): Promise<void> {
    const path = returnTo && returnTo.startsWith('/') ? returnTo : '/';

    // EAA-1878: a sign-in that completed seconds ago and is being asked for again is not a
    // retry, it is the loop. Redirecting would only reload the tab, so stop here and let
    // `/login` render the failure — the same terminating contract `SignInLoopError` already
    // has, applied to the case the attempt counter structurally cannot see (see
    // CALLBACK_COMPLETED_KEY above).
    if (signInJustCompleted()) {
        throw new SignInLoopError();
    }

    if (readLoginAttempts().count >= MAX_LOGIN_ATTEMPTS) {
        // We have asked twice and are still here — asking again would only reload the tab.
        // Hand the failure to the caller to render.
        throw new SignInLoopError();
    }
    recordLoginAttempt();

    await authService.login(path);
}

function loginErrorToFailurePayload(error: unknown, tab: 'staff' | 'tenant'): LoginFailurePayload {
    const apiErr = coerceApiError(error);
    const message = apiErr?.message ?? (error instanceof Error ? error.message : 'Login failed');
    const details = apiErr?.details;
    if (details && Object.keys(details).length > 0) {
        return { message, tab, details };
    }
    return message;
}

/**
 * EAA-1762 addendum: `authConfig.mode === 'password'` fallback for
 * environments (UAT) with no deployed Keycloak/portal — LoginPage renders a
 * form instead of redirecting, and calls this instead of `login()`.
 */
export async function loginWithPassword(payload: LoginPayload): Promise<void> {
    const store = useAuthStore.getState();
    store.loginStart();
    try {
        // rememberMe is UI-only (currently a no-op) — strip before hitting the API.
        const { username, password } = payload;
        const user = await authService.loginWithPassword({ username, password });
        clearLoginAttempts();
        store.loginSuccess(user, 'staff');
    } catch (error) {
        store.loginFailure(loginErrorToFailurePayload(error, 'staff'));
    }
}

/**
 * EAA-1762 addendum: tenant-portal counterpart of `loginWithPassword` —
 * restores the tenant-portal password path (`POST /tenants/portal-login`,
 * never removed from the backend) for the same UAT password-mode fallback.
 */
export async function tenantLogin(payload: TenantPortalLoginPayload): Promise<void> {
    const store = useAuthStore.getState();
    store.loginStart();
    try {
        const user = await authService.loginTenantPortal(payload);
        clearLoginAttempts();
        store.loginSuccess(user, 'tenant');
    } catch (error) {
        store.loginFailure(loginErrorToFailurePayload(error, 'tenant'));
    }
}

/**
 * `/callback` — completes the authorization-code exchange, loads the ACH
 * user (`GET /auth/me` with the new OIDC bearer), and populates the store.
 * Returns the `returnTo` path stashed by `login()`'s `state` so the caller
 * (CallbackPage) can navigate back to where the guard redirected from.
 */
export async function handleOidcCallback(): Promise<string> {
    const store = useAuthStore.getState();
    // EAA-1878: before spending the code, refuse to keep going round. Each lap is
    // a full page load, so an unbounded loop is what freezes the tab; stopping
    // here surfaces it as a message the person can act on (the failure card's
    // "Thử lại" clears this budget) instead of a spinner that never ends.
    if (recordCallbackArrival()) {
        const loop = new SignInLoopError();
        store.loginFailure(loop.message);
        throw loop;
    }
    store.loginStart();
    try {
        const oidcUser = await authClient.handleCallback();
        const user = await authService.getCurrentUser();
        // EAA-1762: the code exchange succeeded, so whatever bounce sequence led
        // here is finished — reset the loop counter before the app navigates on,
        // otherwise a later, unrelated re-login inherits a spent budget.
        //
        // EAA-1878: and record THAT it completed, because clearing the counter is
        // exactly what let the freeze-loop run forever (see CALLBACK_COMPLETED_KEY).
        // Order matters: `clearLoginAttempts()` also drops this marker, so it has
        // to be set afterwards.
        clearLoginAttempts();
        markCallbackCompleted();
        store.loginSuccess(user, 'staff');
        const state = oidcUser.state as { returnTo?: string } | undefined;
        return state?.returnTo || '/';
    } catch (error) {
        store.loginFailure(error instanceof Error ? error.message : 'Sign-in failed');
        throw error;
    }
}

function tenantMeToUser(tenant: Tenant): User {
    return {
        id: '',
        username: '',
        email: '',
        is_superuser: false,
        is_staff: false,
        tenant: {
            id: String(tenant.id),
            slug: tenant.slug,
            name: tenant.name,
        },
    };
}

export async function embedBootstrap(apiToken: string): Promise<void> {
    const store = useAuthStore.getState();
    store.embedBootstrapStart();
    const raw = apiToken.trim();
    try {
        clearEmbedApiToken();
        if (isLikelyTenantJwt(raw)) {
            setEmbedBearerToken(raw);
        } else {
            setEmbedApiToken(raw);
        }
        const tenant = await tenantService.getMe();
        store.embedBootstrapSuccess(tenantMeToUser(tenant));
    } catch {
        clearEmbedApiToken();
        store.embedBootstrapFailure(
            'Invalid or expired embed token. Request a new link from your administrator.'
        );
    }
}

export async function logout(): Promise<void> {
    const store = useAuthStore.getState();
    const { authMode } = store;
    store.logoutStart();
    try {
        if (authMode === 'embed_api') {
            clearEmbedApiToken();
            store.logoutSuccess();
        } else {
            await authService.logout();
            if (authConfig.mode === 'password') {
                // Password mode has nowhere to redirect to — set success directly.
                store.logoutSuccess();
            }
            // EAA-1762: otherwise authService.logout() redirects to Keycloak's
            // RP-initiated logout — the browser navigates away, so there is no
            // "success" state to set here (the next page load re-runs checkAuth).
        }
        // EAA-1670: drop every cached query so a different user logging in
        // within the 30s staleTime window isn't served the previous
        // subject's tenants/content/tags data.
        queryClient.clear();
    } catch (error) {
        store.logoutFailure(error instanceof Error ? error.message : 'Logout failed');
    }
}

/**
 * EAA-755: in-flight guard for checkAuth().
 *
 * A staging HAR showed `GET /api/auth/me` firing twice in the same millisecond on
 * every page load (t+2.04 ×2, t+6.17 ×2). AuthGuard has exactly one mount point and
 * calls checkAuth() from a `useEffect` with `[]` deps, so the pair comes from the
 * guard remounting — a route re-render, or React's double-invoked mount effect.
 *
 * Deduplicating at the call site would only fix the call site we know about; this
 * makes the operation itself idempotent while a check is outstanding, so any number
 * of concurrent callers share one request and one resolution. Deliberately NOT a
 * TanStack Query hook: the auth session is client state owned by the Zustand store
 * (see .cursor/rules/state-management.mdc), and checkAuth is called imperatively
 * from an effect, not during render.
 *
 * The promise is cleared in `finally`, so a LATER check (a real re-verify after the
 * first settles) still hits the network — this collapses concurrency, never caches.
 */
let checkAuthInFlight: Promise<void> | null = null;

export function checkAuth(): Promise<void> {
    if (checkAuthInFlight) return checkAuthInFlight;
    checkAuthInFlight = runCheckAuth().finally(() => {
        checkAuthInFlight = null;
    });
    return checkAuthInFlight;
}

async function runCheckAuth(): Promise<void> {
    const store = useAuthStore.getState();
    store.checkAuthStart();
    try {
        // Embed bootstrap owns its own auth check (EmbedTokenGate); a plain
        // checkAuth() only concerns the staff session (OIDC or, in password
        // mode, the JWT authService already holds — no silent renew to await).
        const accessToken =
            authConfig.mode === 'password'
                ? authService.getAccessToken()
                : await authClient.getAccessToken();
        if (!accessToken) {
            store.checkAuthFailure();
            return;
        }

        // EAA-1762: a usable access token means the session is live — clear the
        // sign-in loop budget here too, since a guard-triggered checkAuth is the
        // normal way a returning bounce ends up authenticated without re-running
        // the callback.
        clearLoginAttempts();

        // EAA-1762 addendum: OIDC has only one non-embed mode ('staff'); password
        // mode may be signed in as either password path, so ask authService which.
        const authMode = authService.getAuthMode();

        // EAA-1886: /me decides the identity; the localStorage copy is only a
        // fallback for when it cannot be reached. This used to be the other way
        // round — a stored user short-circuited the fetch — which made the cache
        // permanent: `is_staff` gates the whole platform-admin surface (the tenant
        // switcher, the all-tenants listing, the tenant column), so a user cached
        // before the backend started reporting it stayed a non-admin until someone
        // cleared localStorage by hand. Serving the stale copy first and patching it
        // afterwards was worse than it looks: the tenant-pinning effect in
        // MainLayout would act on the stale non-staff identity and write that
        // tenant back as the stored preference before the real one arrived.
        let user = null;
        try {
            user = await authService.getCurrentUser();
        } catch {
            user = authService.getStoredUser();
            if (!user) {
                throw new Error('No identity available');
            }
        }
        store.checkAuthSuccess(user, authMode);
    } catch {
        authService.clearAuth();
        store.checkAuthFailure();
    }
}

declare global {
    interface Window {
        __eaa1670AuthListenersRegistered?: boolean;
    }
}

// EAA-1670: guard on a window-scoped flag (survives HMR module
// re-evaluation, unlike a module-scope variable) so Vite HMR doesn't stack
// duplicate listeners — the old eventChannel unregistered on saga teardown;
// a plain module-scope listener has no such lifecycle.
if (typeof window !== 'undefined' && !window.__eaa1670AuthListenersRegistered) {
    window.__eaa1670AuthListenersRegistered = true;
    window.addEventListener('auth:logout', () => {
        clearEmbedApiToken();
        authService.clearAuth();
        useAuthStore.getState().logoutSuccess();
        queryClient.clear();
    });
    window.addEventListener('auth:embed-invalid', () => {
        clearEmbedApiToken();
        useAuthStore
            .getState()
            .embedSessionEnded(
                'Embed session expired or access denied. Reload with a valid token.'
            );
        queryClient.clear();
    });
}
